import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText } from "lucide-react";
import { examsAPI, examQuestionsAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import SelectedQuestionsModal from "./SelectedQuestionsModal";
import EditBasicInfoTab from "./edit-exam-modal/EditBasicInfoTab";
import EditQuestionsTab from "./edit-exam-modal/EditQuestionsTab";
import EditSecurityTab from "./edit-exam-modal/EditSecurityTab";
import EditAdvancedTab from "./edit-exam-modal/EditAdvancedTab";

const EditExamModal = ({ isOpen, onClose, onSuccess, examId }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    totalTime: 60,
    language: "English",
    passPercentage: 50,
    isTemplate: false,
    instructions: "",
    warningsAllowed: 0,
    proctoring: {
      enabled: false,
      captureImages: false,
      trackTabSwitching: false,
      faceDetection: false,
      captureFrequency: 10000,
    },
  });

  // ... keep existing code (state variables and queries)
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [questionPage, setQuestionPage] = useState(1);
  const [questionFilters, setQuestionFilters] = useState({
    search: "",
    questionBankIds: [],
    difficulties: [],
    tags: "",
  });

  const [autoSelections, setAutoSelections] = useState([]);
  const [newAutoSelection, setNewAutoSelection] = useState({
    questionBankIds: [],
    difficulties: {
      easy: 0,
      medium: 0,
      hard: 0,
    },
  });

  const [showSelectedQuestions, setShowSelectedQuestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    data: examData,
    isLoading: examLoading,
    error: examError
  } = useQuery({
    queryKey: ["exam", examId],
    queryFn: () => examsAPI.getOne(examId),
    enabled: isOpen && !!examId,
    staleTime: 0, // Always consider data stale
    cacheTime: 0, // Don't cache the data
    onSuccess: (data) => {
    }
  });

  const {
    data: questionsData,
    isLoading: questionsLoading,
    refetch: refetchQuestions,
  } = useQuery({
    queryKey: ["exam-questions", questionPage, questionFilters],
    queryFn: () => {
      return examQuestionsAPI.getQuestions({
        page: questionPage,
        limit: 10,
        questionBankIds: questionFilters.questionBankIds,
        difficulties: questionFilters.difficulties,
        tags: questionFilters.tags,
        search: questionFilters.search,
      });
    },
    enabled: isOpen,
    keepPreviousData: true,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["exam-question-categories"],
    queryFn: () => examQuestionsAPI.getCategories(),
    enabled: isOpen,
  });

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      totalTime: 60,
      language: "English",
      passPercentage: 50,
      isTemplate: false,
      instructions: "",
      warningsAllowed: 0,
      proctoring: {
        enabled: false,
        captureImages: false,
        trackTabSwitching: false,
        faceDetection: false,
        captureFrequency: 10000,
      },
    });
    setSelectedQuestions([]);
    setAutoSelections([]);
    setQuestionPage(1);
    setQuestionFilters({
      search: "",
      questionBankIds: [],
      difficulties: [],
      tags: "",
    });
    setNewAutoSelection({
      questionBankIds: [],
      difficulties: {
        easy: 0,
        medium: 0,
        hard: 0,
      },
    });
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    } else if (examData?.data) {
      const exam = examData.data;
      
      setFormData({
        name: exam.name || "",
        description: exam.description || "",
        totalTime: exam.totalTime || 60,
        language: exam.language || "English",
        passPercentage: exam.passPercentage || 50,
        isTemplate: exam.isTemplate || false,
        instructions: exam.instructions || "",
        warningsAllowed: exam.warningsAllowed || 0,
        proctoring: {
          enabled: exam.proctoring?.enabled || false,
          captureImages: exam.proctoring?.captureImages !== undefined ? exam.proctoring.captureImages : false,
          trackTabSwitching: exam.proctoring?.trackTabSwitching !== undefined ? exam.proctoring.trackTabSwitching : false,
          faceDetection: exam.proctoring?.faceDetection !== undefined ? exam.proctoring.faceDetection : false,
          captureFrequency: exam.proctoring?.captureFrequency || 10000,
        },
      });

      
      if (exam.questions && Array.isArray(exam.questions)) {
        setSelectedQuestions(exam.questions);
      }
    }
  }, [isOpen, examData]);

  // ... keep existing code (remaining handlers and functions)
  useEffect(() => {
    if (examError) {
      console.error("Error loading exam:", examError);
      toast({
        title: "Error",
        description: "Failed to load exam data",
        variant: "destructive",
      });
      onClose();
    }
  }, [examError, toast, onClose]);

  const handleQuestionToggle = (question) => {
    setSelectedQuestions((prev) => {
      const exists = prev.find((q) => q._id === question._id);
      if (exists) {
        return prev.filter((q) => q._id !== question._id);
      } else {
        return [...prev, question];
      }
    });
  };

  const handleQuestionRemove = (questionId) => {
    setSelectedQuestions((prev) => prev.filter((q) => q._id !== questionId));
  };

  const handleAutoSelectionAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (newAutoSelection.questionBankIds.length === 0) {
      toast({
        title: "Invalid Selection",
        description: "Please select at least one question bank",
        variant: "destructive",
      });
      return;
    }

    const totalQuestions =
      (newAutoSelection.difficulties.easy || 0) +
      (newAutoSelection.difficulties.medium || 0) +
      (newAutoSelection.difficulties.hard || 0);

    if (totalQuestions === 0) {
      toast({
        title: "Invalid Selection",
        description: "Please specify the number of questions needed",
        variant: "destructive",
      });
      return;
    }

    setAutoSelections((prev) => [
      ...prev,
      { ...newAutoSelection, id: Date.now() },
    ]);
    setNewAutoSelection({
      questionBankIds: [],
      difficulties: {
        easy: 0,
        medium: 0,
        hard: 0,
      },
    });
  };

  const handleAutoSelectionRemove = (id) => {
    setAutoSelections((prev) => prev.filter((sel) => sel.id !== id));
  };

  const handleAutoSelectQuestions = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (autoSelections.length === 0) {
      toast({
        title: "No Auto Selections",
        description: "Please add at least one auto selection criteria",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await examQuestionsAPI.autoSelect(autoSelections);
      const autoSelectedQuestions = response.data.selectedQuestions;

      setSelectedQuestions((prev) => {
        const existingIds = prev.map((q) => q._id);
        const newQuestions = autoSelectedQuestions.filter(
          (q) => !existingIds.includes(q._id)
        );
        return [...prev, ...newQuestions];
      });

      setAutoSelections([]);

      toast({
        title: "Questions Auto-Selected",
        description: `${autoSelectedQuestions.length} questions were automatically selected`,
      });
    } catch (error) {
      toast({
        title: "Auto Selection Failed",
        description:
          error.response?.data?.message || "Failed to auto-select questions",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Exam name is required",
        variant: "destructive",
      });
      return;
    }

    if (selectedQuestions.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one question",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await examsAPI.update(examId, {
        ...formData,
        questions: selectedQuestions.map((q) => q._id),
      });

      // Invalidate and refetch exam data
      await queryClient.invalidateQueries(["exam", examId]);
      await queryClient.invalidateQueries(["exams"]);

      toast({
        title: "Success",
        description: "Exam updated successfully",
      });

       if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update exam",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSubCategories = (categoryName) => {
    if (!categoriesData?.data?.categories) return [];
    const category = categoriesData.data.categories.find(
      (cat) => cat.name === categoryName
    );
    return category?.subCategories || [];
  };

  const isExamNameEmpty = !formData.name.trim();

  if (examLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[95vw] h-[95vh] max-w-none flex flex-col overflow-hidden">
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ... keep existing code (return JSX)
  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[95vw] h-[95vh] max-w-none flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0 pb-4">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Edit Exam
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="questions" disabled={isExamNameEmpty}>
                    Questions
                  </TabsTrigger>
                  <TabsTrigger value="security" disabled={isExamNameEmpty}>
                    Security
                  </TabsTrigger>
                  <TabsTrigger value="advanced" disabled={isExamNameEmpty}>
                    Advanced
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4">
                  <EditBasicInfoTab formData={formData} setFormData={setFormData} />
                </TabsContent>

                <TabsContent value="questions" className="space-y-4">
                  <EditQuestionsTab
                    selectedQuestions={selectedQuestions}
                    setShowSelectedQuestions={setShowSelectedQuestions}
                    questionFilters={questionFilters}
                    setQuestionFilters={setQuestionFilters}
                    questionPage={questionPage}
                    setQuestionPage={setQuestionPage}
                    questionsData={questionsData}
                    questionsLoading={questionsLoading}
                    categoriesData={categoriesData}
                    handleQuestionToggle={handleQuestionToggle}
                    getSubCategories={getSubCategories}
                    newAutoSelection={newAutoSelection}
                    setNewAutoSelection={setNewAutoSelection}
                    autoSelections={autoSelections}
                    setAutoSelections={setAutoSelections}
                    handleAutoSelectionAdd={handleAutoSelectionAdd}
                    handleAutoSelectionRemove={handleAutoSelectionRemove}
                    handleAutoSelectQuestions={handleAutoSelectQuestions}
                  />
                </TabsContent>

                <TabsContent value="security" className="space-y-4">
                  <EditSecurityTab formData={formData} setFormData={setFormData} />
                </TabsContent>

                <TabsContent value="advanced" className="space-y-4">
                  <EditAdvancedTab formData={formData} setFormData={setFormData} />
                </TabsContent>
              </Tabs>
            </form>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t flex-shrink-0 mt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={isSubmitting}
              onClick={handleSubmit}
            >
              {isSubmitting ? "Updating..." : "Update Exam"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <SelectedQuestionsModal
        isOpen={showSelectedQuestions}
        onClose={() => setShowSelectedQuestions(false)}
        selectedQuestions={selectedQuestions}
        onRemoveQuestion={handleQuestionRemove}
      />
    </>
  );
};

export default EditExamModal;
